import { mat4, quat, vec3 } from 'gl-matrix';
import { Gltf } from './GltfTypes';
import { MeshTransformExtras, ModelTransformExtras } from './ModelTransformExtras';

const EXTENSION_KEY = '_JOINT_ATTACHMENT';
const BONE_DEFAULT_KEY = '_ATTACHMENT_BONE_DEFAULT';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const warnedNonUuid = new Set<string>();

function warnIfNotUuid(value: string, context: string): void {
  if (UUID_RE.test(value)) return;
  const key = `${context}:${value}`;
  if (warnedNonUuid.has(key)) return;
  warnedNonUuid.add(key);
  console.warn(`[attachment] ${context}: model '${value}' is not a UUID — likely a stale GLB export`);
}

export interface AttachmentTransform {
  position: [number, number, number];
  /** Rotation quaternion [x, y, z, w] (local-space). */
  rotation: [number, number, number, number];
  scale: number;
}

/**
 * An attachment plugged into a bone on this animated model. `transform` is optional:
 * when present it overrides the composed `boneDefault × attachmentModelTransform`
 * chain for this (bone, model, mesh); when absent the runtime uses that composed
 * default.
 */
export interface JointAttachmentDef {
  bone: string;
  model: string;
  mesh: string;
  transform?: AttachmentTransform;
}

/** Per-bone default TRS applied to any attachment plugged into the bone. */
export interface AttachmentBoneDefaultDef {
  bone: string;
  transform: AttachmentTransform;
}

/**
 * Inverse of {@link buildAttachmentOffsetMatrix}: decompose an offset mat4 back into
 * the `AttachmentTransform` TRS form. Useful when seeding a per-attachment override
 * from the currently rendered composed offset (so toggling "add override" doesn't
 * visually jump). Assumes uniform scale and does NOT undo non-uniform scale chains.
 */
export function decomposeAttachmentOffsetMatrix(mat: mat4): AttachmentTransform {
  const rot = mat4.getRotation(quat.create(), mat);
  quat.normalize(rot, rot);
  const scaling = mat4.getScaling(vec3.create(), mat);
  const scale = (scaling[0] + scaling[1] + scaling[2]) / 3;
  const tLocal = vec3.fromValues(mat[12], mat[13], mat[14]);
  const invRot = quat.invert(quat.create(), rot);
  const position = vec3.transformQuat(vec3.create(), tLocal, invRot);
  return {
    position: [position[0], position[1], position[2]],
    rotation: [rot[0], rot[1], rot[2], rot[3]],
    scale,
  };
}

/** Build an offset matrix from a TRS transform with local-space translation. */
export function buildAttachmentOffsetMatrix(out: mat4, transform: AttachmentTransform) {
  const t = vec3.fromValues(...transform.position);
  const r = quat.fromValues(
    transform.rotation[0],
    transform.rotation[1],
    transform.rotation[2],
    transform.rotation[3],
  );
  // Rotate translation into offset's local frame so position values move along rotated axes
  const tLocal = vec3.create();
  vec3.transformQuat(tLocal, t, r);
  const s = transform.scale;
  mat4.fromRotationTranslationScale(out, r, tLocal, vec3.fromValues(s, s, s));
}

/**
 * Compose the final attachment offset matrix from a per-bone default and the
 * attachment GLB's canonical transforms (`_MODEL_TRANSFORM` on the root and
 * `_MESH_TRANSFORM` on the specific mesh). Any layer may be absent; if all are
 * absent, the result is identity.
 *
 * Order matches the render chain: `offset = boneDefault × modelTransform × meshTransform`.
 * The mesh transform moves the mesh into its per-primitive canonical pose, the model
 * transform moves the whole model into a shared canonical pose, and the bone default
 * finally slots that canonical pose at the bone's attachment position.
 */
export function composeAttachmentOffsetMatrix(
  out: mat4,
  boneDefault: AttachmentTransform | null,
  modelTransform: ModelTransformExtras | null,
  meshTransform: MeshTransformExtras | null = null,
): void {
  if (!boneDefault && !modelTransform && !meshTransform) {
    mat4.identity(out);
    return;
  }

  const canonical = buildCanonicalAttachmentMatrix(modelTransform, meshTransform);

  if (!boneDefault) {
    if (canonical) mat4.copy(out, canonical);
    else mat4.identity(out);
    return;
  }

  const boneMat = mat4.create();
  buildAttachmentOffsetMatrix(boneMat, boneDefault);
  if (canonical) mat4.multiply(out, boneMat, canonical);
  else mat4.copy(out, boneMat);
}

/** Compose modelTransform × meshTransform into one canonical pose matrix, or null if both absent. */
function buildCanonicalAttachmentMatrix(
  modelTransform: ModelTransformExtras | null,
  meshTransform: MeshTransformExtras | null,
): mat4 | null {
  if (!modelTransform && !meshTransform) return null;
  const out = mat4.create();
  if (modelTransform) buildModelTransformMatrix(out, modelTransform);
  if (meshTransform) {
    const meshMat = mat4.create();
    buildModelTransformMatrix(meshMat, meshTransform);
    mat4.multiply(out, out, meshMat);
  }
  return out;
}

/**
 * Build a matrix from a model-level TRS (`_MODEL_TRANSFORM` extras). Uses standard
 * TRS semantics — unlike {@link buildAttachmentOffsetMatrix}, the translation is
 * applied in world axes (not rotated into the local frame).
 */
function buildModelTransformMatrix(out: mat4, transform: ModelTransformExtras): void {
  const pos = transform.position ?? [0, 0, 0];
  const rotQuat = transform.rotation ?? [0, 0, 0, 1];
  const scale = transform.scale ?? 1;

  const r = quat.fromValues(rotQuat[0], rotQuat[1], rotQuat[2], rotQuat[3]);
  mat4.fromRotationTranslationScale(
    out,
    r,
    vec3.fromValues(pos[0], pos[1], pos[2]),
    vec3.fromValues(scale, scale, scale),
  );
}

export function readJointAttachments(json: Gltf): JointAttachmentDef[] {
  const skin = json.skins?.[0];
  if (!skin?.extras?.[EXTENSION_KEY]) return [];
  const raw = skin.extras[EXTENSION_KEY];
  if (!Array.isArray(raw)) return [];
  const attachments = raw as JointAttachmentDef[];
  for (const a of attachments) {
    if (typeof a?.model === 'string') warnIfNotUuid(a.model, 'readJointAttachments');
  }
  return attachments;
}

export function writeJointAttachments(json: Gltf, attachments: JointAttachmentDef[]): Gltf {
  return writeSkinExtra(json, EXTENSION_KEY, attachments.length > 0 ? attachments : null);
}

export function readAttachmentBoneDefaults(json: Gltf): AttachmentBoneDefaultDef[] {
  const skin = json.skins?.[0];
  if (!skin?.extras?.[BONE_DEFAULT_KEY]) return [];
  const raw = skin.extras[BONE_DEFAULT_KEY];
  if (!Array.isArray(raw)) return [];
  return raw as AttachmentBoneDefaultDef[];
}

export function writeAttachmentBoneDefaults(
  json: Gltf,
  defaults: AttachmentBoneDefaultDef[],
): Gltf {
  return writeSkinExtra(json, BONE_DEFAULT_KEY, defaults.length > 0 ? defaults : null);
}

function writeSkinExtra(json: Gltf, key: string, value: unknown | null): Gltf {
  const clone = JSON.parse(JSON.stringify(json)) as Gltf;
  const skin = clone.skins?.[0];
  if (!skin) return clone;

  if (!skin.extras) {
    skin.extras = {};
  }

  if (value !== null) {
    skin.extras[key] = value;
  } else {
    delete skin.extras[key];
    if (Object.keys(skin.extras).length === 0) {
      delete skin.extras;
    }
  }

  return clone;
}
